import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ChefHat, ArrowRight, Utensils, X, Sparkles } from "lucide-react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

const MEAL_LABEL = { breakfast: "Breakfast", lunch: "Lunch", dinner: "Dinner", snack: "Snack" };

// Today in the athlete's local timezone as YYYY-MM-DD — matches the tracker's
// date storage so a "Log this" click posts against the correct day.
const localToday = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

// Full-screen modal for the Home "Recipe of the Week" CTA. Shows ingredients,
// method, macros and gives the athlete two options: log the recipe straight
// into today's tracker, or jump to the Recipes hub to explore more.
export default function RecipeOfTheWeekModal({ open, onOpenChange, recipe }) {
  const navigate = useNavigate();
  const [logging, setLogging] = useState(false);
  if (!recipe) return null;

  const photoUrl = recipe.photo
    ? `${API}/community-recipes/photo/${recipe.photo}?auth=${localStorage.getItem("spc_token") || sessionStorage.getItem("spc_token") || ""}`
    : null;

  const macros = [
    recipe.kcal != null && { l: "kcal", v: recipe.kcal, c: "#E2E30A" },
    recipe.protein != null && { l: "P", v: `${recipe.protein}g`, c: "#4C9AFF" },
    recipe.carb != null && { l: "C", v: `${recipe.carb}g`, c: "#34C759" },
    recipe.fat != null && { l: "F", v: `${recipe.fat}g`, c: "#FF9500" },
  ].filter(Boolean);

  const canLog = recipe.kcal != null || recipe.protein != null || recipe.carb != null || recipe.fat != null;

  const logToday = async () => {
    if (logging) return;
    setLogging(true);
    try {
      await api.post("/tracker/log", {
        date: localToday(),
        meal: recipe.meal || "snack",
        name: recipe.name,
        qty: 1, unit: "serving",
        kcal: recipe.kcal || 0,
        protein: recipe.protein || 0,
        carb: recipe.carb || 0,
        fat: recipe.fat || 0,
        source: "recipe-of-the-week",
      });
      toast.success(`Logged "${recipe.name}" to today's ${MEAL_LABEL[recipe.meal]?.toLowerCase() || "snack"}`);
      onOpenChange(false);
    } catch (e) {
      toast.error("Couldn't log — please try again.");
    } finally {
      setLogging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="recipe-of-the-week-modal"
        className="bg-[#0F0F0F] border border-[#E2E30A]/40 rounded-none text-white max-w-2xl p-0 gap-0 max-h-[90vh] overflow-hidden flex flex-col"
        style={{ boxShadow: "0 0 0 1px rgba(226,227,10,0.15), 0 20px 60px rgba(0,0,0,0.7)" }}>
        {photoUrl ? (
          <div className="w-full h-52 sm:h-64 shrink-0 bg-black overflow-hidden relative">
            <img src={photoUrl} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F0F0F] via-[#0F0F0F]/40 to-transparent" />
            {recipe.is_new && (
              <span data-testid="recipe-of-the-week-new-badge" className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black bg-[#E2E30A] px-2 py-1">
                <Sparkles className="h-3 w-3" /> New
              </span>
            )}
          </div>
        ) : (
          <div className="w-full h-40 shrink-0 bg-[#141414] grid place-items-center relative">
            <ChefHat className="h-12 w-12 text-white/10" />
            {recipe.is_new && (
              <span data-testid="recipe-of-the-week-new-badge" className="absolute top-3 left-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-black bg-[#E2E30A] px-2 py-1">
                <Sparkles className="h-3 w-3" /> New
              </span>
            )}
          </div>
        )}
        <button type="button" data-testid="recipe-of-the-week-modal-close"
          onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 h-8 w-8 grid place-items-center bg-black/70 border border-white/15 text-zinc-200 hover:text-white hover:border-white/40 transition-colors z-10">
          <X className="h-4 w-4" />
        </button>

        <div className="flex-1 min-h-0 overflow-y-auto">
          <div className="px-5 sm:px-6 pt-5 pb-4">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">Recipe of the Week</p>
            <h2 data-testid="recipe-of-the-week-modal-name" className="font-display text-2xl sm:text-3xl uppercase tracking-tight mt-1.5 leading-tight">{recipe.name}</h2>
            <div className="flex items-center flex-wrap gap-2 mt-2">
              <span className="text-[11px] uppercase tracking-wider text-zinc-300">{MEAL_LABEL[recipe.meal] || recipe.meal}</span>
              {(recipe.tags || []).slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] uppercase tracking-wider text-zinc-300 border border-white/15 px-1.5 py-0.5">{t}</span>
              ))}
              <span className="inline-flex items-center gap-1 text-[11px] font-mono font-bold text-[#FF3B30] border border-[#FF3B30]/30 px-2 py-0.5 ml-auto">
                <Heart className="h-3 w-3 fill-current" /> {recipe.helpful}
              </span>
            </div>

            {macros.length > 0 && (
              <div data-testid="recipe-of-the-week-modal-macros" className="flex flex-wrap items-baseline gap-4 mt-4 pt-4 border-t border-white/10 font-mono text-sm tabular-nums">
                {macros.map((m) => (
                  <span key={m.l} className="inline-flex items-baseline gap-1.5">
                    <span className="text-white text-base font-bold">{m.v}</span>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: m.c }}>{m.l}</span>
                  </span>
                ))}
              </div>
            )}

            {(recipe.ingredients || []).length > 0 && (
              <div className="mt-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#4C9AFF] font-bold">Ingredients</p>
                <ul data-testid="recipe-of-the-week-modal-ingredients" className="text-sm text-zinc-200 mt-2 space-y-1 list-disc pl-5">
                  {recipe.ingredients.map((x, i) => <li key={`${i}-${x.slice(0, 12)}`}>{x}</li>)}
                </ul>
              </div>
            )}

            {(recipe.method || []).length > 0 && (
              <div className="mt-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#E2E30A] font-bold">Method</p>
                <ol data-testid="recipe-of-the-week-modal-method" className="text-sm text-zinc-200 mt-2 space-y-1.5 list-decimal pl-5">
                  {recipe.method.map((x, i) => <li key={`${i}-${x.slice(0, 12)}`}>{x}</li>)}
                </ol>
              </div>
            )}

            <p className="text-[10px] text-zinc-500 mt-5">Shared by a member of the community.</p>
          </div>
        </div>

        <div className="border-t border-white/10 bg-[#0A0A0A] p-4 grid sm:grid-cols-2 gap-2.5 shrink-0">
          <button type="button" data-testid="recipe-of-the-week-modal-log"
            onClick={logToday} disabled={!canLog || logging}
            className="group inline-flex items-center justify-center gap-2 bg-[#E2E30A] hover:bg-[#c9ca09] disabled:bg-[#3a3a0a] disabled:text-zinc-600 disabled:cursor-not-allowed text-black font-bold uppercase tracking-wider text-xs py-3 px-4 transition-colors">
            <Utensils className="h-4 w-4" />
            {logging ? "Logging…" : "Log this recipe"}
          </button>
          <button type="button" data-testid="recipe-of-the-week-modal-view"
            onClick={() => { onOpenChange(false); navigate(`/recipes?highlight=${recipe.id}`); }}
            className="group inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/40 hover:bg-white/[0.03] text-white uppercase tracking-wider text-xs py-3 px-4 transition-colors">
            View in Recipe Hub <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
